import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowRight,
    ShieldCheck,
    PhoneCall,
    ListChecks,
    Map,
    KeyRound,
    Truck,
    Boxes,
    Sparkles,
    Users,
    Handshake,
    Flag,
    Home as HomeIcon,
    CheckCircle2,
} from "lucide-react";
import { findService } from "@/data/serviceCatalog";
import { Reveal } from "@/components/site/Reveal";
import CartoonSticker from "@/components/site/CartoonStickers";

const PRICING_TIERS = [
    {
        title: "Free Consultation",
        price: "Free",
        unit: " · 20 min",
        accent: "emerald",
        badge: "Start here",
        bullets: ["Understand your move", "Recommend the next step"],
        cta: { label: "Book Free", to: "/book?service=free_consultation" },
    },
    {
        title: "Paid Consultation",
        price: "$75",
        unit: "/hr",
        accent: "navy",
        bullets: ["Detailed planning", "Timeline & options", "Recommended scope"],
        cta: { label: "Book Consultation", to: "/book?service=paid_consultation" },
    },
    {
        title: "Shortlisting Support",
        price: "$150",
        unit: " · 2 hrs",
        accent: "highlight",
        badge: "Popular",
        bullets: ["Curated rental options", "Match location & budget"],
        cta: { label: "Request Shortlisting", to: "/book?service=shortlisting" },
    },
    {
        title: "Viewing on Behalf",
        price: "$300",
        unit: " · 4 hrs · 3 viewings",
        accent: "white",
        bullets: ["We attend in person", "Practical observations", "Photos/videos where permitted"],
        cta: { label: "Request Viewing", to: "/book?service=viewing_on_behalf" },
    },
    {
        title: "Move-Day Coordination",
        price: "From $200",
        unit: " · 2-10 hr packages",
        accent: "navy",
        bullets: ["On-the-ground supervision", "Vendor + helper coordination"],
        cta: { label: "Book Move-Day", to: "/book?service=move_day_4h" },
    },
    {
        title: "Packing & Cleaning",
        price: "Quote",
        unit: " · scope based",
        accent: "white",
        bullets: ["Vendor coordination", "Move-in / move-out cleaning"],
        cta: { label: "Request Quote", to: "/book?service=packing_support" },
    },
];

function PricingCard({ tier }) {
    const accentMap = {
        emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
        navy: "bg-[#0E2A47]/5 text-[#0E2A47] border-[#0E2A47]/15",
        highlight: "bg-[#FDF1E7] text-[#F26B1F] border-[#F26B1F]/30",
        white: "bg-white text-[#0E2A47] border-[#ECE4D3]",
    };
    return (
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="card-soft relative flex h-full flex-col gap-4 overflow-hidden p-6"
            data-testid={`pricing-card-${tier.title.toLowerCase().replaceAll(" ", "-")}`}
        >
            {tier.badge && (
                <span className={`absolute right-4 top-4 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${accentMap[tier.accent]}`}>
                    {tier.badge}
                </span>
            )}
            <h3 className="font-display text-lg font-semibold text-[#0E2A47]">{tier.title}</h3>
            <div className="flex items-baseline gap-1">
                <span className="font-display text-3xl font-extrabold text-[#0E2A47]">{tier.price}</span>
                <span className="text-sm text-[#475569]">{tier.unit}</span>
            </div>
            <ul className="space-y-2 text-sm text-[#475569]">
                {tier.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                        <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-[#F26B1F]" />
                        <span>{b}</span>
                    </li>
                ))}
            </ul>
            <Link
                to={tier.cta.to}
                className="mt-auto inline-flex items-center gap-1 text-sm font-semibold text-[#F26B1F] hover:gap-2"
            >
                {tier.cta.label} <ArrowRight size={14} />
            </Link>
        </motion.div>
    );
}

const ICON_MAP = {
    "free-consultation": PhoneCall,
    "paid-consultation": ListChecks,
    shortlisting: Map,
    viewing: KeyRound,
    "landlord-connection": Handshake,
    "move-day": Truck,
    packing: Boxes,
    cleaning: Sparkles,
    "vendor-coordination": Users,
};

// Compact zig-zag: 3 stops per row, 2 rows. End marker after row 2.
const ROADMAP_ROWS = [
    ["free-consultation", "paid-consultation", "shortlisting"],
    ["viewing", "move-day", "packing"],
];

// Quote-only services shown as a compact side-strip below the main path.
const SIDE_BRANCHES = ["landlord-connection", "vendor-coordination", "cleaning"];

const TAG_STYLES = {
    FREE: "bg-emerald-50 text-emerald-700",
    PAID: "bg-[#FDF1E7] text-[#F26B1F]",
    QUOTE: "bg-[#0E2A47]/5 text-[#0E2A47]",
};

export default function Services() {
    return (
        <div data-testid="services-page">
            {/* HERO */}
            <section className="section relative gradient-warm overflow-hidden pb-12 md:pb-16">
                <span className="float-y absolute -top-10 right-10 h-40 w-40 rounded-full bg-[#F26B1F]/10 blur-3xl" />
                <CartoonSticker name="boxes" width={88} rotate={-6} className="hidden xl:inline-flex top-12 right-6" />
                <CartoonSticker name="house" width={76} rotate={8} className="hidden xl:inline-flex bottom-6 left-4" />

                <div className="container-atm relative">
                    <Reveal>
                        <span className="eyebrow">Practical support</span>
                        <h1 className="mt-4 max-w-3xl text-4xl font-semibold leading-[1.05] sm:text-5xl lg:text-6xl">
                            Your moving journey,{" "}
                            <span className="bg-gradient-to-r from-[#F26B1F] to-[#FF8E47] bg-clip-text text-transparent">
                                step by step.
                            </span>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="mt-5 max-w-2xl text-base text-[#475569] md:text-lg">
                            Every move starts with a free consultation. From there, we guide
                            you through planning, shortlisting, viewings, move-day and
                            on-the-ground support — all connected as one clear path. Tap any
                            stop to dive in.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* COMPACT ROADMAP */}
            <section className="pb-16 pt-2 md:pt-4">
                <div className="container-atm">
                    <RoadmapZigZag />
                </div>
            </section>

            {/* PRICING — moved here from /pricing route */}
            <section id="pricing" className="section gradient-cream">
                <div className="container-atm">
                    <Reveal>
                        <span className="eyebrow">Transparent pricing</span>
                        <h2 className="mt-3 max-w-3xl font-display text-3xl font-semibold leading-[1.1] text-[#0E2A47] sm:text-4xl lg:text-5xl">
                            Clear price. No hidden fees.
                        </h2>
                        <p className="mt-4 max-w-2xl text-[15px] text-[#475569]">
                            Free where it should be, fixed where we can, quote-based where
                            the scope depends on your move. Additional time is always billed
                            with your approval first.
                        </p>
                    </Reveal>

                    <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {PRICING_TIERS.map((t) => (
                            <PricingCard key={t.title} tier={t} />
                        ))}
                    </div>

                    <Reveal delay={0.15}>
                        <p className="mt-8 max-w-2xl text-sm text-[#475569]">
                            Payments are handled securely via Moneris (Canadian payment
                            processor). Free consultations require no payment. Cancellation
                            policy and timing extensions are confirmed during booking.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* SERVICE BOUNDARIES */}
            <section className="section">
                <div className="container-atm">
                    <Reveal>
                        <div className="card-3d mx-auto max-w-3xl p-8 md:p-10">
                            <div className="flex items-start gap-4">
                                <span className="rounded-2xl bg-[#0E2A47] p-3 text-white">
                                    <ShieldCheck size={22} strokeWidth={1.6} />
                                </span>
                                <div>
                                    <h3 className="text-xl font-semibold">
                                        Service boundaries
                                    </h3>
                                    <p className="mt-3 text-[15px] leading-relaxed text-[#475569]">
                                        AllTask Moves provides coordination and practical
                                        support. We do not provide legal, immigration, real
                                        estate brokerage, financial, property inspection,
                                        guaranteed housing, or international freight services.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}

/* ---------- Compact zig-zag roadmap ---------- */
function RoadmapZigZag() {
    return (
        <div className="relative">
            {/* Top header row: start + end markers on the same line for visual context */}
            <div className="mb-6 flex items-center justify-between gap-4">
                <Reveal>
                    <div className="flex items-center gap-3">
                        <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#F26B1F] text-white shadow-[0_10px_22px_-10px_rgba(242,107,31,0.65)]">
                            <Flag size={16} strokeWidth={2} />
                        </span>
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#F26B1F]">
                                Start
                            </p>
                            <p className="text-sm font-semibold text-[#0E2A47]">
                                Free consultation
                            </p>
                        </div>
                    </div>
                </Reveal>
                <Reveal delay={0.1}>
                    <div className="flex items-center gap-3 text-right">
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#0E2A47]">
                                Move-in day
                            </p>
                            <p className="text-sm font-semibold text-[#0E2A47]">
                                Welcome to the GTA
                            </p>
                        </div>
                        <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#0E2A47] text-white shadow-[0_10px_22px_-10px_rgba(14,42,71,0.5)]">
                            <HomeIcon size={16} strokeWidth={2} />
                        </span>
                    </div>
                </Reveal>
            </div>

            {/* ROW 1 — left to right */}
            <RoadmapRow rowIndex={0} slugs={ROADMAP_ROWS[0]} direction="right" startNumber={1} />

            {/* Dashed connector between rows */}
            <div className="my-5 hidden items-center gap-3 md:flex" aria-hidden="true">
                <span className="h-px flex-1 border-t-2 border-dashed border-[#F26B1F]/40" />
                <ArrowRight size={14} className="text-[#F26B1F]/60" />
                <span className="h-px flex-1 border-t-2 border-dashed border-[#F26B1F]/40" />
            </div>
            {/* Spacer on mobile so rows stack */}
            <div className="my-4 block h-px bg-gradient-to-r from-transparent via-[#F26B1F]/40 to-transparent md:hidden" />

            {/* ROW 2 — right to left (zig-zag) */}
            <RoadmapRow rowIndex={1} slugs={ROADMAP_ROWS[1]} direction="left" startNumber={4} />

            {/* QUOTE-BASED SIDE BRANCHES — compact strip */}
            <div className="mt-10">
                <Reveal>
                    <div className="mb-4 flex items-center gap-2">
                        <span className="h-px flex-1 bg-[#ECE4D3]" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#0E2A47]/50">
                            Quote-based add-ons
                        </span>
                        <span className="h-px flex-1 bg-[#ECE4D3]" />
                    </div>
                </Reveal>
                <div className="grid gap-3 sm:grid-cols-3">
                    {SIDE_BRANCHES.map((slug) => (
                        <SideBranchChip key={slug} slug={slug} />
                    ))}
                </div>
            </div>
        </div>
    );
}

function RoadmapRow({ slugs, direction, startNumber }) {
    // Show slugs left-to-right always, but flip the connector arrows direction
    // visually so the row reads as zig-zag.
    const items = slugs.map((slug, i) => ({ slug, stepNumber: startNumber + i }));
    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-3 md:gap-4">
            {items.map((it, i) => (
                <div key={it.slug} className="relative">
                    {/* connector between cards on desktop */}
                    {i > 0 && (
                        <div
                            aria-hidden="true"
                            className="pointer-events-none absolute left-0 top-1/2 hidden h-px w-3 -translate-x-3 -translate-y-1/2 bg-gradient-to-r from-[#F26B1F]/40 to-[#F26B1F]/40 sm:block"
                        />
                    )}
                    <CompactCard slug={it.slug} stepNumber={it.stepNumber} dir={direction} />
                </div>
            ))}
        </div>
    );
}

function CompactCard({ slug, stepNumber }) {
    const service = findService(slug);
    if (!service) return null;
    const Icon = ICON_MAP[slug] || ListChecks;
    const tag = TAG_STYLES[service.tag] || TAG_STYLES.PAID;

    return (
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
            <Link
                to={`/services/${service.slug}`}
                data-testid={`roadmap-step-${service.slug}`}
                className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-[#ECE4D3] bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-[#F26B1F] hover:shadow-[0_18px_40px_-22px_rgba(14,42,71,0.25)] md:p-5"
            >
                <span className="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-gradient-to-r from-[#F26B1F] to-[#FFA66E] transition-transform duration-500 group-hover:scale-x-100" />

                <div className="flex items-center justify-between gap-2">
                    <span className="inline-flex rounded-xl bg-[#0E2A47] p-2 text-white transition-transform duration-500 group-hover:rotate-6">
                        <Icon size={16} strokeWidth={1.8} />
                    </span>
                    <span
                        className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest ${tag}`}
                    >
                        Step {String(stepNumber).padStart(2, "0")} · {service.tag}
                    </span>
                </div>

                <h3 className="mt-3 font-display text-base font-semibold leading-snug text-[#0E2A47] md:text-[17px]">
                    {service.title}
                </h3>
                <p className="mt-1.5 line-clamp-2 text-[13px] leading-relaxed text-[#475569]">
                    {service.summary}
                </p>

                <div className="mt-3 flex items-center justify-between">
                    <span className="font-display text-base font-semibold text-[#0E2A47]">
                        {service.price}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#F26B1F] transition-all group-hover:gap-2">
                        Learn more <ArrowRight size={12} />
                    </span>
                </div>
            </Link>
        </motion.div>
    );
}

function SideBranchChip({ slug }) {
    const service = findService(slug);
    if (!service) return null;
    const Icon = ICON_MAP[slug] || ListChecks;
    const tag = TAG_STYLES[service.tag] || TAG_STYLES.QUOTE;

    return (
        <Link
            to={`/services/${service.slug}`}
            data-testid={`roadmap-branch-${service.slug}`}
            className="group flex items-center gap-3 rounded-xl border border-dashed border-[#ECE4D3] bg-[#FAF6EE]/60 px-3 py-2.5 transition-all hover:-translate-y-0.5 hover:border-[#F26B1F] hover:bg-white"
        >
            <span className="inline-flex shrink-0 rounded-lg bg-white p-1.5 text-[#F26B1F] shadow-sm">
                <Icon size={14} strokeWidth={1.8} />
            </span>
            <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-semibold text-[#0E2A47]">
                    {service.title}
                </p>
                <span className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest ${tag}`}>
                    {service.tag}
                </span>
            </div>
            <ArrowRight size={12} className="text-[#0E2A47]/40 transition-all group-hover:translate-x-0.5 group-hover:text-[#F26B1F]" />
        </Link>
    );
}
